 import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { LoginSocialGoogle, LoginSocialFacebook, LoginSocialTwitter, LoginSocialLinkedin } from 'reactjs-social-login';
import { X } from 'lucide-react';
import { getAuth, signInWithPopup, OAuthProvider } from 'firebase/auth';
import { initializeApp } from 'firebase/app';

const LoginPopup = ({ isOpen, onClose }) => {
  const [provider, setProvider] = useState('');
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showManualForm, setShowManualForm] = useState(false);
  const [hoverState, setHoverState] = useState({});

  const REDIRECT_URI = window.location.href;

  // Firebase configuration
  const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
  };
  const firebaseApp = initializeApp(firebaseConfig);
  const auth = getAuth(firebaseApp);

  // Inline styles with default and hover states
  const styles = {
    "popup-overlay": {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 50,
    },
    "popup-container": {
      background: "white",
      padding: "2rem",
      borderRadius: "0.5rem",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      width: "100%",
      maxWidth: "400px",
      position: "relative",
    },
    "close-button": {
      position: "absolute",
      top: "0.5rem",
      right: "0.5rem",
      color: hoverState["close-button"] ? "#111827" : "#4b5563",
      background: "none",
      border: "none",
      cursor: "pointer",
    },
    "popup-title": {
      fontSize: "1.5rem",
      fontWeight: "bold",
      marginBottom: "1.5rem",
      textAlign: "center",
      color: "#1f2937",
    },
    "error-message": {
      color: "#dc2626",
      marginBottom: "1rem",
      textAlign: "center",
    },
    "logged-in": {
      textAlign: "center",
    },
    "logged-in-text": {
      marginBottom: "1rem",
      color: "#374151",
    },
    "logout-button": {
      background: hoverState["logout-button"] ? "#c2410c" : "#F97316",
      color: "white",
      padding: "0.5rem 1rem",
      borderRadius: "0.25rem",
      border: "none",
      cursor: "pointer",
      fontSize: "1rem",
    },
    "login-options": {
      display: "flex",
      flexDirection: "column",
      gap: "1rem",
    },
    "google-login-button": {
      width: "100%",
      padding: "0.5rem",
      borderRadius: "0.25rem",
      border: "none",
      cursor: "pointer",
      fontSize: "1rem",
      background: hoverState["google-login-button"] ? "#3578E5" : "#4285F4",
      color: "white",
    },
    "facebook-login-button": {
      width: "100%",
      padding: "0.5rem",
      borderRadius: "0.25rem",
      border: "none",
      cursor: "pointer",
      fontSize: "1rem",
      background: hoverState["facebook-login-button"] ? "#2f477a" : "#3b5998",
      color: "white",
    },
    "twitter-login-button": {
      width: "100%",
      padding: "0.5rem",
      borderRadius: "0.25rem",
      border: "none",
      cursor: "pointer",
      fontSize: "1rem",
      background: hoverState["twitter-login-button"] ? "#1a91da" : "#1DA1F2",
      color: "white",
    },
    "linkedin-login-button": {
      width: "100%",
      padding: "0.5rem",
      borderRadius: "0.25rem",
      border: "none",
      cursor: "pointer",
      fontSize: "1rem",
      background: hoverState["linkedin-login-button"] ? "#005e8a" : "#0077B5",
      color: "white",
    },
    "apple-login-button": {
      width: "100%",
      padding: "0.5rem",
      borderRadius: "0.25rem",
      border: "none",
      cursor: "pointer",
      fontSize: "1rem",
      background: hoverState["apple-login-button"] ? "#1f2937" : "black",
      color: "white",
    },
    "email-login-button": {
      width: "100%",
      padding: "0.5rem",
      borderRadius: "0.25rem",
      border: "none",
      cursor: "pointer",
      fontSize: "1rem",
      background: hoverState["email-login-button"] ? "#c2410c" : "#F97316",
      color: "white",
    },
    "manual-login-form": {
      display: "flex",
      flexDirection: "column",
      gap: "1rem",
    },
    "form-group": {
      display: "flex",
      flexDirection: "column",
    },
    "form-label": {
      fontSize: "0.875rem",
      fontWeight: 500,
      color: "#374151",
      marginBottom: "0.25rem",
    },
    "form-input": {
      width: "100%",
      padding: "0.5rem 0.75rem",
      border: "1px solid #d1d5db",
      borderRadius: "0.25rem",
      fontSize: "1rem",
      outline: hoverState["form-input"] ? "none" : undefined,
      borderColor: hoverState["form-input"] ? "#F97316" : undefined,
      boxShadow: hoverState["form-input"] ? "0 0 0 2px #F97316" : undefined,
    },
    "submit-button": {
      background: hoverState["submit-button"] ? "#c2410c" : "#F97316",
      color: "white",
      padding: "0.5rem",
      borderRadius: "0.25rem",
      border: "none",
      cursor: "pointer",
      fontSize: "1rem",
    },
    "back-button": {
      color: hoverState["back-button"] ? "#7c2d12" : "#F97316",
      background: "none",
      border: "none",
      cursor: "pointer",
      textAlign: "center",
      textDecoration: hoverState["back-button"] ? "underline" : "none",
      fontSize: "0.875rem",
    },
  };

  // Hover handlers
  const handleMouseEnter = (key) => {
    setHoverState((prev) => ({ ...prev, [key]: true }));
  };
  const handleMouseLeave = (key) => {
    setHoverState((prev) => ({ ...prev, [key]: false }));
  };

  const onLoginStart = useCallback(() => {
    console.log('Login started');
  }, []);

  const onLogoutSuccess = useCallback(() => {
    setProfile(null);
    setProvider('');
    setError(null);
    setShowManualForm(false);
    console.log('Logout successful');
  }, []);

  const sendToBackend = async (provider, data) => {
    try {
      const customer = {
        name: data.name || 'Unknown',
        email: data.email || '',
        provider: provider,
        providerId: data.sub || data.id || data.user_id || data.uid || '',
        oauthToken: data.access_token || data.id_token || '',
        role: 'ROLE_USER',
        active: 1,
      };

      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/customers/register`, customer);
      console.log('Customer registered:', response.data);
      setProfile({ ...data, provider });
      setProvider(provider);
      setError(null);
      onClose();
    } catch (err) {
      console.error('Error registering customer:', err);
      setError('Failed to register with backend');
    }
  };

  const handleManualLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, { email, password });
      console.log('Manual login successful:', response.data);
      setProfile({ email, name: 'Manual User' });
      setProvider('manual');
      setError(null);
      onClose();
    } catch (err) {
      console.error('Manual login failed:', err);
      setError('Invalid email or password');
    }
  };

  const handleAppleLogin = async () => {
    try {
      const provider = new OAuthProvider('apple.com');
      const result = await signInWithPopup(auth, provider);
      const user = result.user || {};
      const data = {
        name: user.displayName || 'Unknown',
        email: user.email || '',
        uid: user.uid || '',
        id_token: result._tokenResponse ? result._tokenResponse.idToken : '',
      };
      sendToBackend('apple', data);
    } catch (err) {
      console.error('Apple login failed:', err);
      setError('Apple login failed');
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles["popup-overlay"]}>
      <div style={styles["popup-container"]}>
        <button
          onClick={onClose}
          style={styles["close-button"]}
          onMouseEnter={() => handleMouseEnter("close-button")}
          onMouseLeave={() => handleMouseLeave("close-button")}
        >
          <X size={24} />
        </button>
        <h2 style={styles["popup-title"]}>Login to Your Account</h2>
        {error && <p style={styles["error-message"]}>{error}</p>}
        {profile && provider ? (
          <div style={styles["logged-in"]}>
            <p style={styles["logged-in-text"]}>Logged in as {profile.name || 'User'} ({provider})</p>
            <button
              onClick={onLogoutSuccess}
              style={styles["logout-button"]}
              onMouseEnter={() => handleMouseEnter("logout-button")}
              onMouseLeave={() => handleMouseLeave("logout-button")}
            >
              Logout
            </button>
          </div>
        ) : (
          <>
            {!showManualForm ? (
              <div style={styles["login-options"]}>
                <LoginSocialGoogle
                  client_id={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}
                  onLoginStart={onLoginStart}
                  onResolve={({ provider, data }) => sendToBackend(provider, data)}
                  onReject={(err) => {
                    console.error(err);
                    setError('Google login failed');
                  }}
                >
                  <button
                    style={styles["google-login-button"]}
                    onMouseEnter={() => handleMouseEnter("google-login-button")}
                    onMouseLeave={() => handleMouseLeave("google-login-button")}
                  >
                    Sign in with Google
                  </button>
                </LoginSocialGoogle>
                <LoginSocialFacebook
                  appId={process.env.REACT_APP_FACEBOOK_APP_ID || ''}
                  onLoginStart={onLoginStart}
                  onResolve={({ provider, data }) => sendToBackend(provider, data)}
                  onReject={(err) => {
                    console.error(err);
                    setError('Facebook login failed');
                  }}
                >
                  <button
                    style={styles["facebook-login-button"]}
                    onMouseEnter={() => handleMouseEnter("facebook-login-button")}
                    onMouseLeave={() => handleMouseLeave("facebook-login-button")}
                  >
                    Sign in with Facebook
                  </button>
                </LoginSocialFacebook>
                <LoginSocialTwitter
                  client_id={process.env.REACT_APP_TWITTER_V2_APP_KEY || ''}
                  client_secret={process.env.REACT_APP_TWITTER_V2_APP_SECRET || ''}
                  redirect_uri={REDIRECT_URI}
                  onLoginStart={onLoginStart}
                  onResolve={({ provider, data }) => sendToBackend(provider, data)}
                  onReject={(err) => {
                    console.error(err);
                    setError('Twitter login failed');
                  }}
                >
                  <button
                    style={styles["twitter-login-button"]}
                    onMouseEnter={() => handleMouseEnter("twitter-login-button")}
                    onMouseLeave={() => handleMouseLeave("twitter-login-button")}
                  >
                    Sign in with Twitter
                  </button>
                </LoginSocialTwitter>
                <LoginSocialLinkedin
                  client_id={process.env.REACT_APP_LINKEDIN_CLIENT_ID || ''}
                  client_secret={process.env.REACT_APP_LINKEDIN_CLIENT_SECRET || ''}
                  redirect_uri={REDIRECT_URI}
                  onLoginStart={onLoginStart}
                  onResolve={({ provider, data }) => sendToBackend(provider, data)}
                  onReject={(err) => {
                    console.error(err);
                    setError('LinkedIn login failed');
                  }}
                >
                  <button
                    style={styles["linkedin-login-button"]}
                    onMouseEnter={() => handleMouseEnter("linkedin-login-button")}
                    onMouseLeave={() => handleMouseLeave("linkedin-login-button")}
                  >
                    Sign in with LinkedIn
                  </button>
                </LoginSocialLinkedin>
                <button
                  onClick={handleAppleLogin}
                  style={styles["apple-login-button"]}
                  onMouseEnter={() => handleMouseEnter("apple-login-button")}
                  onMouseLeave={() => handleMouseLeave("apple-login-button")}
                >
                  Sign in with Apple
                </button>
                <button
                  onClick={() => setShowManualForm(true)}
                  style={styles["email-login-button"]}
                  onMouseEnter={() => handleMouseEnter("email-login-button")}
                  onMouseLeave={() => handleMouseLeave("email-login-button")}
                >
                  Login with Email
                </button>
              </div>
            ) : (
              <form onSubmit={handleManualLogin} style={styles["manual-login-form"]}>
                <div style={styles["form-group"]}>
                  <label htmlFor="email" style={styles["form-label"]}>
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={styles["form-input"]}
                    onFocus={() => handleMouseEnter("form-input")}
                    onBlur={() => handleMouseLeave("form-input")}
                  />
                </div>
                <div style={styles["form-group"]}>
                  <label htmlFor="password" style={styles["form-label"]}>
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    style={styles["form-input"]}
                    onFocus={() => handleMouseEnter("form-input")}
                    onBlur={() => handleMouseLeave("form-input")}
                  />
                </div>
                <button
                  type="submit"
                  style={styles["submit-button"]}
                  onMouseEnter={() => handleMouseEnter("submit-button")}
                  onMouseLeave={() => handleMouseLeave("submit-button")}
                >
                  Login
                </button>
                <button
                  type="button"
                  onClick={() => setShowManualForm(false)}
                  style={styles["back-button"]}
                  onMouseEnter={() => handleMouseEnter("back-button")}
                  onMouseLeave={() => handleMouseLeave("back-button")}
                >
                  Back to Social Login
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LoginPopup;